import React, { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  OutlinedInput,
  Box,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

/**
 * Props:
 *   selectedInput:    string (currently chosen deviceId)
 *   setSelectedInput: (deviceId: string) => void
 *
 * Usage notes:
 *   - On first render (mount), this component will:
 *       1. Call getUserMedia({ audio: true }) to force the browser to pick the OS default mic.
 *       2. Read that track’s settings.deviceId and call setSelectedInput(defaultDeviceId).
 *       3. Stop that temporary stream.
 *       4. Then enumerate all audioinput devices and populate the dropdown.
 *
 *   - When the user picks a new deviceId from the dropdown, handleInputChange:
 *       1. Immediately does a getUserMedia({ audio: { deviceId: { exact: newDeviceId } } })
 *          so the browser will confirm permissions (or re-use an existing grant) on that specific mic.
 *       2. Stops that new stream’s tracks (unless you actually intend to use that stream here).
 *       3. Calls setSelectedInput(newDeviceId), updating the parent state.
 */
export default function AudioInputDeviceSelector({
  selectedInput,
  setSelectedInput,
}) {
  const [inputDevices, setInputDevices] = useState([]);

  useEffect(() => {
    async function fetchDevicesAndDefault() {
      // 1. Request a generic mic stream so labels appear AND browser picks your OS default device:
      let tempStream;
      try {
        tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.warn(
          'User denied initial mic access; labels may be blank, and we cannot detect default mic.',
          err
        );
      }

      // 2. If we got a stream, determine which deviceId the browser actually used:
      if (tempStream) {
        const audioTrack = tempStream.getAudioTracks()[0];
        if (audioTrack) {
          const settings = audioTrack.getSettings();
          const defaultDeviceId = settings.deviceId;
          // Only override if parent hasn’t already set something
          if (!selectedInput) {
            setSelectedInput(defaultDeviceId);
          }
        }
        // 3. Stop that temporary stream
        tempStream.getTracks().forEach((t) => t.stop());
      }

      // 4. Now enumerate all audioinput devices (labels will be populated if mic perms were granted)
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = allDevices.filter((d) => d.kind === 'audioinput');
      setInputDevices(audioInputs);
    }

    fetchDevicesAndDefault();
    // We only want to run this ONCE at mount, so we pass an empty deps array.
    // (We purposely do NOT include selectedInput/setSelectedInput here
    //  to avoid re-running if the parent changes selectedInput.)
  }, []);

  // Whenever the user picks a new mic from the dropdown:
  const handleInputChange = async (event) => {
    const newDeviceId = event.target.value;

    // 1. Request permission for that specific mic:
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: newDeviceId } },
      });
      // We now have a stream from the newly selected device.
      // If you don’t actually need to route audio to it here, immediately stop it:
      newStream.getTracks().forEach((t) => t.stop());
    } catch (err) {
      console.error('Unable to access the newly selected microphone:', err);
      // You may want to reset the dropdown or alert the user here.
      return;
    }

    // 2. Inform parent of the new selection:
    setSelectedInput(newDeviceId);
  };

  if (!inputDevices.length) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ width: 250 }}
      >
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="output-device-label">Input Device</InputLabel>
          <OutlinedInput
            value={'Loading input devices ...'}
            disabled={true}
            startAdornment={
              <InputAdornment position="start">
                <MicIcon />
              </InputAdornment>
            }
          />
        </FormControl>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ width: 250 }} // adjust as needed
    >
      <FormControl variant="outlined" fullWidth>
        <InputLabel id="input-device-label">Input Device</InputLabel>
        <Select
          labelId="input-device-label"
          id="input-device-select"
          variant={'outlined'}
          value={selectedInput || ''}
          onChange={handleInputChange}
          label="Input Device"
          input={
            <OutlinedInput
              // The MicIcon on the left:
              startAdornment={
                <InputAdornment position="start">
                  <MicIcon />
                </InputAdornment>
              }
              notched
            />
          }
        >
          {inputDevices.map((device) => (
            <MenuItem key={device.deviceId} value={device.deviceId}>
              {device.label || 'Unknown Microphone'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
